import { freshState, normalize } from "@hachi/core";
import type { Car, Entry, GarageState } from "@hachi/core";

/**
 * Keeps the local garage and the account's `records` table in step, record by record, exactly
 * like the current app. Hashes must stay identical to production: a device's saved `-sync` base
 * is compared against them, and any difference would re-upload a person's whole garage.
 */

export type RecordKind = "car" | "entry" | "settings" | "meta";

export interface RemoteRow {
  kind: RecordKind;
  id: string;
  data: unknown;
  deleted?: boolean;
  updated_at: string;
}

interface LocalRecord {
  kind: RecordKind;
  id: string;
  data: unknown;
}

interface PushRow extends LocalRecord {
  deleted?: boolean;
}

/** `hachi-garage-v1-sync`: the hash of each record at the last sync, and how far pulls have reached. */
export interface SyncState {
  uid?: string;
  base?: Record<string, string>;
  cursor?: string | null;
}

/** Meta values that belong to this device and are never uploaded. */
const LOCAL_META = new Set(["lastAutoPoint", "lastBackup", "changedSinceBackup", "updatedAt", "notify", "notifyHour"]);

/** Later pulls re-read this far back, catching a slow save whose time is older than the cursor. Same as production. */
export const PULL_OVERLAP_MS = 5 * 60e3;
export const PUSH_CHUNK = 500;
export const PULL_PAGE = 2000;
const MAX_PAGES = 100;

function stable(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stable).join(",")}]`;
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o).filter((k) => o[k] !== undefined).sort().map((k) => `${JSON.stringify(k)}:${stable(o[k])}`).join(",")}}`;
  }
  return JSON.stringify(v === undefined ? null : v);
}

export function hashOf(v: unknown): string {
  const s = stable(v);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36) + s.length.toString(36);
}

/** The state as plain JSON, without the helper fields the current app stores on entries. */
const cleanCopy = <T,>(st: T): T => JSON.parse(JSON.stringify(st, (k, v: unknown) => (k === "_g" || k === "_d" ? undefined : v))) as T;

export function collect(st: GarageState): Map<string, LocalRecord> {
  const m = new Map<string, LocalRecord>();
  const c = cleanCopy(st);
  for (const car of c.cars) m.set(`car:${car.id}`, { kind: "car", id: car.id, data: car });
  for (const e of c.log) m.set(`entry:${e.id}`, { kind: "entry", id: e.id, data: e });
  m.set("settings:settings", { kind: "settings", id: "settings", data: c.settings });
  const meta: Record<string, unknown> = {};
  for (const k of Object.keys(c.meta ?? {})) if (!LOCAL_META.has(k)) meta[k] = c.meta[k];
  m.set("meta:meta", { kind: "meta", id: "meta", data: meta });
  return m;
}

/** How many records differ from the last sync. Zero before the first sync, as today. */
export function pendingCount(st: GarageState, sync: SyncState): number {
  const base = sync.base;
  if (!base) return 0;
  const cur = collect(st);
  let n = 0;
  for (const [k, r] of cur) if (base[k] !== hashOf(r.data)) n++;
  for (const k of Object.keys(base)) if (!cur.has(k)) n++;
  return n;
}

/**
 * Applies pulled rows. A record changed on this device since the last sync wins over the incoming
 * one. Updates `sync.base` in place; returns the new state, or the same object if nothing changed.
 */
export function applyRemote(st: GarageState, rows: readonly RemoteRow[], sync: SyncState & { base: Record<string, string> }): GarageState {
  const dirty = new Set<string>();
  for (const [k, r] of collect(st)) if (sync.base[k] !== hashOf(r.data)) dirty.add(k);
  let changed = false;
  const cars = new Map(st.cars.map((c) => [c.id, c]));
  const log = new Map(st.log.map((e) => [e.id, e]));
  let { settings, meta } = st;
  for (const r of rows) {
    const k = `${r.kind}:${r.id}`;
    if (dirty.has(k)) continue;
    if (r.deleted) {
      if (r.kind === "car" && cars.delete(r.id)) changed = true;
      if (r.kind === "entry" && log.delete(r.id)) changed = true;
      delete sync.base[k];
      continue;
    }
    const h = hashOf(r.data);
    if (sync.base[k] === h && (r.kind !== "car" || cars.has(r.id)) && (r.kind !== "entry" || log.has(r.id))) continue;
    if (r.kind === "car") cars.set(r.id, r.data as Car);
    else if (r.kind === "entry") log.set(r.id, r.data as Entry);
    else if (r.kind === "settings") settings = { ...settings, ...(r.data as object) };
    else if (r.kind === "meta") meta = { ...meta, ...(r.data as object) };
    sync.base[k] = h;
    changed = true;
  }
  if (!changed) return st;
  return { ...st, settings, meta, cars: [...cars.values()], log: [...log.values()].filter((e) => cars.has(e.car)) };
}

/**
 * First sync on a device: account data wins, things made here that the account doesn't have are
 * kept, and sample data only stays when the account has no cars.
 */
export function mergeFirstSync(st: GarageState, rows: readonly RemoteRow[], uid: string): { state: GarageState; sync: SyncState & { base: Record<string, string> } } {
  const live = rows.filter((r) => !r.deleted);
  const gone = new Set(rows.filter((r) => r.deleted).map((r) => `${r.kind}:${r.id}`));
  const last = rows[rows.length - 1];
  const sync = { uid, base: {} as Record<string, string>, cursor: last ? last.updated_at : null };
  if (!live.length) return { state: st, sync };
  const rc = new Map<string, Car>(), rl = new Map<string, Entry>();
  let rs: object | null = null, rm: object | null = null;
  for (const r of live) {
    sync.base[`${r.kind}:${r.id}`] = hashOf(r.data);
    if (r.kind === "car") rc.set(r.id, r.data as Car);
    else if (r.kind === "entry") rl.set(r.id, r.data as Entry);
    else if (r.kind === "settings") rs = r.data as object;
    else if (r.kind === "meta") rm = r.data as object;
  }
  const remoteHasCars = rc.size > 0;
  for (const c of st.cars) if (!rc.has(c.id) && !gone.has(`car:${c.id}`) && (!c.sample || !remoteHasCars)) rc.set(c.id, c);
  for (const e of st.log) if (!rl.has(e.id) && !gone.has(`entry:${e.id}`) && rc.has(e.car) && (!e.sample || !remoteHasCars)) rl.set(e.id, e);
  const state = normalize({
    ...st, cars: [...rc.values()], log: [...rl.values()],
    settings: rs ? { ...st.settings, ...(rs as object) } : st.settings,
    meta: rm ? { ...st.meta, ...(rm as object) } : st.meta,
  }, st);
  return { state, sync };
}

/** What to upload: changed records and tombstones, cars before their entries, entry deletions before car deletions. */
export function pushRows(st: GarageState, base: Record<string, string>): { rows: PushRow[]; hashes: Record<string, string | null> } {
  const cur = collect(st), rows: PushRow[] = [], hashes: Record<string, string | null> = {};
  for (const [k, r] of cur) {
    const h = hashOf(r.data);
    if (base[k] !== h) { rows.push(r); hashes[k] = h; }
  }
  for (const k of Object.keys(base)) {
    if (cur.has(k)) continue;
    const [kind, ...rest] = k.split(":");
    rows.push({ kind: kind as RecordKind, id: rest.join(":"), data: null, deleted: true });
    hashes[k] = null;
  }
  const order = (r: PushRow) => (r.deleted ? (r.kind === "entry" ? 0 : 1) : r.kind === "car" ? 2 : r.kind === "entry" ? 3 : 4);
  rows.sort((a, b) => order(a) - order(b));
  return { rows, hashes };
}

interface RefusedRow { kind: string; id: string }

export class SyncError extends Error {
  /** `missing`: the database hasn't got this function yet. */
  constructor(readonly code: "signed-out" | "device" | "server" | "network" | "missing") {
    super(code);
    this.name = "SyncError";
  }
}

export interface SyncDeps {
  /** Calls a database function with the signed-in user's token. Throws SyncError or a network error. */
  rpc: <T>(fn: "sync_pull" | "sync_push" | "sync_push_checked", args: Record<string, unknown>) => Promise<T>;
  deviceId: () => string;
  userId: () => string | null;
  getState: () => GarageState;
  /** Replaces the local garage after a sync brought changes. */
  setState: (st: GarageState) => void;
  loadSync: () => SyncState;
  saveSync: (s: SyncState) => void;
  /** Sync started, finished or the pending count may have changed. */
  onStatus?: () => void;
  /** A different person signed in on this browser and their garage replaced the old one. */
  onUserChanged?: () => void;
  /** The server says this device or session isn't valid any more. */
  onRejected?: () => void;
}

/** Runs one sync at a time; a request during a run schedules another right after it. */
export function createSync(d: SyncDeps) {
  let running: Promise<void> | null = null;
  let again = false;
  let lastOk = 0;
  let lastErr = "";
  let checked = true;
  let refused = 0;

  /** Uploads one batch and returns what the plan's limit refused. Falls back while the database update isn't run yet. */
  async function pushChunk(chunk: PushRow[]): Promise<RefusedRow[]> {
    const args = { p_device: d.deviceId(), p_rows: chunk };
    if (checked) {
      try {
        return (await d.rpc<{ refused?: RefusedRow[] }>("sync_push_checked", args)).refused ?? [];
      } catch (e) {
        if (!(e instanceof SyncError && e.code === "missing")) throw e;
        checked = false;
      }
    }
    await d.rpc("sync_push", args);
    return [];
  }

  async function pullAll(since: string | null): Promise<RemoteRow[]> {
    const rows: RemoteRow[] = [];
    let cur = since;
    for (let i = 0; i < MAX_PAGES; i++) {
      const page = await d.rpc<RemoteRow[]>("sync_pull", { p_device: d.deviceId(), p_since: cur });
      rows.push(...page);
      const last = page[page.length - 1];
      if (page.length < PULL_PAGE || !last) break;
      cur = last.updated_at;
    }
    return rows;
  }

  async function push(sync: SyncState & { base: Record<string, string> }) {
    const { rows, hashes } = pushRows(d.getState(), sync.base);
    let held = 0;
    for (let i = 0; i < rows.length; i += PUSH_CHUNK) {
      const chunk = rows.slice(i, i + PUSH_CHUNK);
      const no = new Set((await pushChunk(chunk)).map((r) => `${r.kind}:${r.id}`));
      held += no.size;
      for (const r of chunk) {
        const k = `${r.kind}:${r.id}`, h = hashes[k];
        // Refused: it stays pending here and goes up by itself after an upgrade.
        if (no.has(k)) continue;
        if (h == null) delete sync.base[k];
        else sync.base[k] = h;
      }
      d.saveSync(sync);
    }
    refused = held;
  }

  async function once(uid: string) {
    let sync = d.loadSync();
    if (!sync.base || sync.uid !== uid) {
      // Someone else's garage was here: start clean.
      if (sync.uid && sync.uid !== uid) {
        const clean = freshState();
        d.setState({ ...clean, meta: { ...clean.meta, welcomed: false } });
        d.onUserChanged?.();
      }
      const rows = await pullAll(null);
      // Merged against the garage as it is now, so anything added during the pull is kept.
      const merged = mergeFirstSync(d.getState(), rows, uid);
      if (merged.state !== d.getState()) d.setState(merged.state);
      d.saveSync(merged.sync);
      sync = merged.sync;
    }
    const s = sync as SyncState & { base: Record<string, string> };
    await push(s);
    const since = s.cursor ? new Date(Date.parse(s.cursor) - PULL_OVERLAP_MS).toISOString() : null;
    const rows = await pullAll(since);
    const last = rows[rows.length - 1];
    if (last) {
      const before = d.getState();
      const after = applyRemote(before, rows, s);
      if (!s.cursor || last.updated_at > s.cursor) s.cursor = last.updated_at;
      d.saveSync(s);
      if (after !== before) d.setState(after);
    }
  }

  /** Syncs now. Never rejects; check `status()` for the outcome. */
  function now(): Promise<void> {
      if (running) { again = true; return running; }
      const uid = d.userId();
      if (!uid) return Promise.resolve();
      running = (async () => {
        d.onStatus?.();
        try {
          await once(uid);
          lastOk = Date.now();
          lastErr = "";
        } catch (e) {
          lastErr = e instanceof SyncError ? e.code : "network";
          if (lastErr === "device" || lastErr === "signed-out") d.onRejected?.();
        } finally {
          running = null;
          d.onStatus?.();
          if (again) { again = false; void now(); }
        }
      })();
      return running;
  }

  return {
    now,
    status() {
      return { running: !!running, lastOk, lastErr, refused };
    },
  };
}

export type Sync = ReturnType<typeof createSync>;

/** Status wording for Settings, and the short version for the footer (`syncText`, `syncShort`). */
export function syncText(n: number, online: boolean, s: { running: boolean; lastOk: number; lastErr: string }): { long: string; short: string } {
  const changes = `${n} ${n === 1 ? "change" : "changes"}`;
  if (!online) return n ? { long: `Offline. ${changes} will sync when you're back online.`, short: "Offline. Will sync later." } : { long: "Offline. Everything is saved on this device.", short: "Offline. Saved here." };
  if (s.running) return { long: "Syncing…", short: "Saving…" };
  if (s.lastErr && n) return { long: `Couldn't sync ${changes} yet. Trying again soon.`, short: "Not synced yet. Retrying…" };
  if (n) return { long: "Saving to your account…", short: "Saving…" };
  return { long: s.lastOk ? "All changes saved to your account" : "Saved to your account", short: "All changes saved." };
}
